import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { IBrand } from '@principle-theorem/principle-core/interfaces';
import {
  PrimaryKey,
  WithRef,
  snapshotCombineLatest,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PracticeMigration } from '../../practice-migrations';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';

export interface IPatientRelationshipJobData<
  SourcePatient extends ISourceEntityTypes,
> {
  brand: WithRef<IBrand>;
  sourcePatient: IGetRecordResponse<SourcePatient>;
}

/**
 * Abstract base for destination entities that perform a second-pass patch
 * on patient records (e.g. relationships, referrers).
 *
 * To filter which source records are included in the job (e.g. only patients
 * with a referrer), override `_filterSourceRecords`.
 */
export abstract class BasePatientRelationshipDestinationEntity<
  SourcePatient extends ISourceEntityTypes,
  DestinationRecord extends object,
  MigrationData extends object,
> extends BaseDestinationEntity<
  DestinationRecord,
  IPatientRelationshipJobData<SourcePatient>,
  MigrationData
> {
  abstract patientSourceEntity: ISourceEntityHandler<SourcePatient>;

  sourceCountDataAccessor(
    data: IPatientRelationshipJobData<SourcePatient>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(
    data: IPatientRelationshipJobData<SourcePatient>
  ): string {
    return data.sourcePatient.record.uid;
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: MigrationData
  ): MigrationData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientRelationshipJobData<SourcePatient>,
    _migrationData: MigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientRelationshipJobData<SourcePatient>[]> {
    const brand$ = PracticeMigration.brand$(migration);
    return snapshotCombineLatest([brand$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([patientRecords, [brand]]) =>
        this._filterSourceRecords(patientRecords).map((sourcePatient) => ({
          sourcePatient,
          brand,
        }))
      )
    );
  }

  buildSkippedResponse(
    data: IPatientRelationshipJobData<SourcePatient>
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<SourcePatient>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage: string
  ): IDestinationEntityRecord<DestinationRecord> &
    FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage,
      failData: {
        patientRef: patient.id,
      },
    } as IDestinationEntityRecord<DestinationRecord> &
      FailedDestinationEntityRecord;
  }

  /**
   * Override to filter which source patient records are included in the job.
   * Default: include all records. Example override: filter to only patients
   * with a resolvable referrer.
   */
  protected _filterSourceRecords(
    records: IGetRecordResponse<SourcePatient>[]
  ): IGetRecordResponse<SourcePatient>[] {
    return records;
  }
}
